import React, { useState, useEffect } from 'react';
import supabase from '../../utils/supabaseUtils';
import './ContactsList.css';

const ContactsList = () => {
  const [contacts, setContacts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showContactForm, setShowContactForm] = useState(false);
  const [contactToEdit, setContactToEdit] = useState(null);
  const [formValues, setFormValues] = useState({
    first_name: '',
    last_name: '',
    email: '',
    phone: '',
    organization: '',
    title: '',
    type: 'team_contact',
    notes: ''
  });
  const [searchTerm, setSearchTerm] = useState('');
  const [successMessage, setSuccessMessage] = useState('');
  const [filterType, setFilterType] = useState('all');

  useEffect(() => {
    fetchContacts();
  }, []);

  const fetchContacts = async () => {
    setLoading(true);
    try {
      // In a production environment, we would fetch contacts from Supabase
      // For development, we'll use mock data
      const mockContacts = [
        {
          id: '1',
          first_name: 'Sarah',
          last_name: 'Johnson',
          email: 'sarah.johnson@example.com',
          phone: '(303) 555-1234',
          organization: 'Denver Vikings',
          title: 'Team Manager',
          type: 'team_contact',
          notes: 'Main point of contact for Denver Vikings football team',
          created_at: '2023-05-15T08:30:00Z'
        },
        {
          id: '2',
          first_name: 'Michael',
          last_name: 'Rodriguez',
          email: 'mrodriguez@example.com',
          phone: '(720) 555-5678',
          organization: 'Colorado Rapids Youth',
          title: 'Youth Program Director',
          type: 'club_contact',
          notes: 'Manages all youth programs for Colorado Rapids',
          created_at: '2023-04-20T10:15:00Z'
        },
        {
          id: '3',
          first_name: 'Jessica',
          last_name: 'Williams',
          email: 'jwilliams@tournament.com',
          phone: '(970) 555-9876',
          organization: 'Colorado State Championship',
          title: 'Event Coordinator',
          type: 'event_contact',
          notes: 'Coordinator for the annual state championship',
          created_at: '2023-03-10T14:45:00Z'
        },
        {
          id: '4',
          first_name: 'David',
          last_name: 'Thompson',
          email: 'dthompson@eagleshigh.edu',
          phone: '(303) 555-3344',
          organization: 'Eagles High School',
          title: 'Athletic Director',
          type: 'school_contact',
          notes: 'Oversees all sports programs at Eagles High School',
          created_at: '2023-05-05T09:20:00Z'
        },
        {
          id: '5',
          first_name: 'Amanda',
          last_name: 'Chen',
          email: 'achen@bolderbasketball.org',
          phone: '(720) 555-2233',
          organization: 'Boulder Basketball Club',
          title: 'Club President',
          type: 'club_contact',
          notes: 'President of the Boulder basketball organization',
          created_at: '2023-04-12T11:30:00Z'
        }
      ];
      
      setContacts(mockContacts);
      setLoading(false);
    } catch (error) {
      console.error('Error fetching contacts:', error);
      setLoading(false);
    }
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormValues({
      ...formValues,
      [name]: value
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setSuccessMessage('');
    
    try {
      // Validate required fields
      if (!formValues.first_name || !formValues.last_name) {
        alert("First name and last name are required");
        setLoading(false);
        return;
      }
      
      if (contactToEdit) {
        // Update existing contact
        const updatedContacts = contacts.map(contact => 
          contact.id === contactToEdit.id 
            ? { ...contact, ...formValues }
            : contact
        );
        setContacts(updatedContacts);
        
        // In production, you would update the contact in Supabase
        // const { error } = await supabase
        //   .from('contacts')
        //   .update(formValues)
        //   .eq('id', contactToEdit.id);
        
        setSuccessMessage(`${formValues.first_name} ${formValues.last_name} updated successfully!`);
      } else {
        // Create new contact
        const newContact = {
          id: crypto.randomUUID(),
          ...formValues,
          created_at: new Date().toISOString()
        };
        
        setContacts([newContact, ...contacts]);
        
        // In production, you would insert the contact in Supabase
        // const { error } = await supabase
        //   .from('contacts')
        //   .insert(newContact);
        
        setSuccessMessage(`${formValues.first_name} ${formValues.last_name} added successfully!`);
      }
      
      // Reset form after short delay to show success message
      setTimeout(() => {
        resetForm();
      }, 1500);
    } catch (error) {
      console.error('Error saving contact:', error);
      alert(`Error: ${error.message}`);
    } finally {
      setLoading(false);
    }
  };

  const resetForm = () => {
    setFormValues({
      first_name: '',
      last_name: '',
      email: '',
      phone: '',
      organization: '',
      title: '',
      type: 'team_contact',
      notes: ''
    });
    setContactToEdit(null);
    setShowContactForm(false);
    setSuccessMessage('');
  };

  const handleEditContact = (contact) => {
    setContactToEdit(contact);
    setFormValues({
      first_name: contact.first_name,
      last_name: contact.last_name,
      email: contact.email,
      phone: contact.phone,
      organization: contact.organization,
      title: contact.title,
      type: contact.type,
      notes: contact.notes
    });
    setShowContactForm(true);
  };

  const handleDeleteContact = async (contactId) => {
    if (!confirm("Are you sure you want to delete this contact?")) {
      return;
    }
    
    try {
      // Remove from UI for immediate feedback
      setContacts(contacts.filter(contact => contact.id !== contactId));
      
      // In production, you would delete from Supabase
      // const { error } = await supabase
      //   .from('contacts')
      //   .delete()
      //   .eq('id', contactId);
      
      alert("Contact deleted successfully");
    } catch (error) {
      console.error("Error deleting contact:", error);
      alert(`Error: ${error.message}`);
      fetchContacts(); // Refresh to restore data
    }
  };

  // Filter contacts by type and search term
  const filteredContacts = contacts.filter(contact => {
    const matchesSearch = 
      contact.first_name.toLowerCase().includes(searchTerm.toLowerCase()) ||
      contact.last_name.toLowerCase().includes(searchTerm.toLowerCase()) ||
      contact.organization.toLowerCase().includes(searchTerm.toLowerCase()) ||
      contact.email.toLowerCase().includes(searchTerm.toLowerCase());
    
    const matchesFilter = filterType === 'all' || contact.type === filterType;
    
    return matchesSearch && matchesFilter;
  });

  // Format phone number for display
  const formatPhone = (phone) => {
    if (!phone) return '';
    
    // Simple formatting - this could be enhanced for different formats
    return phone;
  };

  // Format contact type for display
  const formatContactType = (type) => {
    if (!type) return '';
    
    const types = {
      'team_contact': 'Team',
      'club_contact': 'Club',
      'event_contact': 'Event',
      'school_contact': 'School',
      'venue_contact': 'Venue',
      'other': 'Other'
    };
    
    return types[type] || type.replace('_', ' ');
  };

  return (
    <div className="contacts-container">
      <div className="section-header">
        <h3>Contact Management</h3>
        <div className="header-actions">
          <button 
            className="refresh-button"
            onClick={fetchContacts}
            title="Refresh Contacts"
          >
            <i className="fas fa-sync-alt"></i>
          </button>
          <button 
            className="add-button"
            onClick={() => {
              setContactToEdit(null);
              setFormValues({
                first_name: '',
                last_name: '',
                email: '',
                phone: '',
                organization: '',
                title: '',
                type: 'team_contact',
                notes: ''
              });
              setShowContactForm(true);
            }}
          >
            <i className="fas fa-user-plus"></i> Add Contact
          </button>
        </div>
      </div>
      
      {/* Search and filter controls */}
      <div className="contacts-controls">
        <div className="search-box">
          <i className="fas fa-search"></i>
          <input
            type="text"
            placeholder="Search contacts..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
          />
          {searchTerm && (
            <button 
              className="clear-search" 
              onClick={() => setSearchTerm('')}
              title="Clear search"
            >
              <i className="fas fa-times"></i>
            </button>
          )}
        </div>
        
        <div className="filter-controls">
          <select 
            value={filterType} 
            onChange={(e) => setFilterType(e.target.value)}
          >
            <option value="all">All Types</option>
            <option value="team_contact">Team Contacts</option>
            <option value="club_contact">Club Contacts</option>
            <option value="event_contact">Event Contacts</option>
            <option value="school_contact">School Contacts</option>
            <option value="venue_contact">Venue Contacts</option>
            <option value="other">Other Contacts</option>
          </select>
        </div>
      </div>
      
      {/* Contacts List */}
      {loading && contacts.length === 0 ? (
        <div className="loading-container">
          <div className="spinner"></div>
          <p>Loading contacts...</p>
        </div>
      ) : filteredContacts.length === 0 ? (
        <div className="empty-state">
          <div className="empty-icon">
            <i className="fas fa-address-book"></i>
          </div>
          <h3>No Contacts Found</h3>
          <p>{searchTerm || filterType !== 'all' ? 'Try adjusting your search or filters' : 'Add your first contact to get started'}</p>
          {!searchTerm && filterType === 'all' && (
            <button 
              className="add-button"
              onClick={() => {
                setContactToEdit(null);
                setFormValues({
                  first_name: '',
                  last_name: '',
                  email: '',
                  phone: '',
                  organization: '',
                  title: '',
                  type: 'team_contact',
                  notes: ''
                });
                setShowContactForm(true);
              }}
            >
              <i className="fas fa-user-plus"></i> Add Contact
            </button>
          )}
        </div>
      ) : (
        <div className="contacts-table-container">
          <table className="contacts-table">
            <thead>
              <tr>
                <th>Name</th>
                <th>Organization</th>
                <th>Title</th>
                <th>Contact</th>
                <th>Type</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {filteredContacts.map(contact => (
                <tr key={contact.id}>
                  <td className="contact-name">
                    <div className="avatar">
                      {`${contact.first_name.charAt(0)}${contact.last_name.charAt(0)}`}
                    </div>
                    <div className="name-details">
                      <div className="full-name">
                        {`${contact.first_name} ${contact.last_name}`}
                      </div>
                    </div>
                  </td>
                  <td>{contact.organization || '—'}</td>
                  <td>{contact.title || '—'}</td>
                  <td>
                    <div className="contact-info">
                      <div>
                        <i className="fas fa-envelope"></i> {contact.email}
                      </div>
                      {contact.phone && (
                        <div>
                          <i className="fas fa-phone"></i> {formatPhone(contact.phone)}
                        </div>
                      )}
                    </div>
                  </td>
                  <td>
                    <span className={`contact-type ${contact.type}`}>
                      {formatContactType(contact.type)}
                    </span>
                  </td>
                  <td>
                    <div className="action-buttons">
                      <button 
                        className="action-button view"
                        onClick={() => alert(`View details for ${contact.first_name}`)}
                        title="View Contact Details"
                      >
                        <i className="fas fa-eye"></i>
                      </button>
                      <button 
                        className="action-button edit"
                        onClick={() => handleEditContact(contact)}
                        title="Edit Contact"
                      >
                        <i className="fas fa-edit"></i>
                      </button>
                      <button 
                        className="action-button delete"
                        onClick={() => handleDeleteContact(contact.id)}
                        title="Delete Contact"
                      >
                        <i className="fas fa-trash-alt"></i>
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
      
      {/* Contact Form Modal */}
      {showContactForm && (
        <div className="modal-overlay">
          <div className="contact-form-modal">
            <div className="modal-header">
              <h3>{contactToEdit ? 'Edit Contact' : 'Add New Contact'}</h3>
              <button 
                className="close-button"
                onClick={resetForm}
                disabled={loading}
              >
                ×
              </button>
            </div>
            
            {successMessage && (
              <div className="success-message">
                <i className="fas fa-check-circle"></i>
                {successMessage}
              </div>
            )}
            
            <form onSubmit={handleSubmit}>
              <div className="form-row">
                <div className="form-group">
                  <label>First Name</label>
                  <input
                    type="text"
                    name="first_name"
                    value={formValues.first_name}
                    onChange={handleInputChange}
                    required
                    disabled={loading}
                  />
                </div>
                
                <div className="form-group">
                  <label>Last Name</label>
                  <input
                    type="text"
                    name="last_name"
                    value={formValues.last_name}
                    onChange={handleInputChange}
                    required
                    disabled={loading}
                  />
                </div>
              </div>
              
              <div className="form-row">
                <div className="form-group">
                  <label>Email</label>
                  <input
                    type="email"
                    name="email"
                    value={formValues.email}
                    onChange={handleInputChange}
                    disabled={loading}
                  />
                </div>
                
                <div className="form-group">
                  <label>Phone</label>
                  <input
                    type="text"
                    name="phone"
                    value={formValues.phone}
                    onChange={handleInputChange}
                    disabled={loading}
                  />
                </div>
              </div>
              
              <div className="form-row">
                <div className="form-group">
                  <label>Organization</label>
                  <input
                    type="text"
                    name="organization"
                    value={formValues.organization}
                    onChange={handleInputChange}
                    disabled={loading}
                  />
                </div>
                
                <div className="form-group">
                  <label>Title</label>
                  <input
                    type="text"
                    name="title"
                    value={formValues.title}
                    onChange={handleInputChange}
                    disabled={loading}
                  />
                </div>
              </div>
              
              <div className="form-group">
                <label>Contact Type</label>
                <select
                  name="type"
                  value={formValues.type}
                  onChange={handleInputChange}
                  disabled={loading}
                >
                  <option value="team_contact">Team Contact</option>
                  <option value="club_contact">Club Contact</option>
                  <option value="event_contact">Event Contact</option>
                  <option value="school_contact">School Contact</option>
                  <option value="venue_contact">Venue Contact</option>
                  <option value="other">Other</option>
                </select>
              </div>
              
              <div className="form-group">
                <label>Notes</label>
                <textarea
                  name="notes"
                  value={formValues.notes}
                  onChange={handleInputChange}
                  rows="3"
                  disabled={loading}
                ></textarea>
              </div>
              
              <div className="form-buttons">
                <button 
                  type="button" 
                  className="cancel-button"
                  onClick={resetForm}
                  disabled={loading}
                >
                  Cancel
                </button>
                <button 
                  type="submit" 
                  className="submit-button"
                  disabled={loading}
                >
                  {loading ? (
                    <span className="loading-text">
                      <span className="spinner-small"></span>
                      {contactToEdit ? 'Updating...' : 'Creating...'}
                    </span>
                  ) : (
                    contactToEdit ? 'Update Contact' : 'Create Contact'
                  )}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default ContactsList; 